import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Service areas — layar `areas` paket client.
 *
 * Prototipe punya kolom Emirate dan Radius (km). Location tidak menyimpan
 * keduanya — yang tersimpan sebuah poligon. Radius diganti luas sebenarnya,
 * dan emirat tidak ditebak: kolom description-lah satu-satunya keterangan
 * wilayah yang ada, dan isinya bebas.
 *
 * Aksi "Edit boundary" prototipe membuka DrawingManager Google Maps. Peta itu
 * berbayar per pemuatan dan sudah pernah ditolak untuk situs publik, jadi di
 * sini poligonnya ditampilkan sebagai gambar SVG dari titik-titiknya sendiri
 * dan disunting sebagai teks koordinat — bentuk yang sama yang sudah diterima
 * addLocation. Cukup untuk melihat sebuah zona rusak atau kebesaran, tanpa
 * menambah tagihan.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String,
                  $state: String, $priced: String) {
  getAdminServiceAreas(currentPage: $currentPage, limit: $limit, searchList: $searchList,
                       state: $state, priced: $priced) {
    count total activeCount providersOutsideEveryZone status errorMessage
    results {
      id name description isActive vertices areaKm2 centreLat centreLng
      providerCount pricingCount activePricingCount outline
      lockedOffReason lockedDeleteReason
    }
  }
}`;

const M_SAVE = `mutation ($id: Int, $LocationName: String!, $coordinates: String!,
                 $description: String!, $isActive: Boolean) {
  addLocation(id: $id, LocationName: $LocationName, coordinates: $coordinates,
              description: $description, isActive: $isActive) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int) {
  deleteLocation(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const km2 = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });

/*
 * Bentuk zona digambar dari titiknya sendiri: kotak pembatas poligon
 * dinormalkan ke dalam viewBox, jadi tiap zona terlihat sebesar kotaknya
 * sendiri. Yang dicari di sini bentuknya — kotak lima titik, atau garis besar
 * sungguhan — bukan letaknya di peta.
 */
const Outline = ({ points, size = 180 }) => {
  const pts = String(points || '').trim().split(/\s+/)
    .map(p => p.split(',').map(Number))
    .filter(p => p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]));
  if (pts.length < 3) return <span className="muted">No boundary</span>;

  const lats = pts.map(p => p[0]);
  const lngs = pts.map(p => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const w = (maxLng - minLng) || 1e-6;
  const h = (maxLat - minLat) || 1e-6;
  const pad = 4;
  const scale = Math.min((size - pad * 2) / w, (size - pad * 2) / h);

  const d = pts.map(([lat, lng]) => [
    pad + (lng - minLng) * scale,
    // Lintang naik ke utara, sumbu y SVG naik ke bawah.
    pad + (maxLat - lat) * scale
  ].map(v => Math.round(v * 10) / 10).join(',')).join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}
      style={{ maxWidth: '100%' }} aria-label="Zone outline">
      <polygon points={d} fill="color-mix(in srgb, var(--brand-amber) 18%, transparent)"
        stroke="var(--brand-amber)" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

class ServiceAreas extends React.Component {
  state = {
    rows: [], count: 0, total: 0, activeCount: 0, outside: 0,
    page: 1, perPage: 25, search: '', visibility: '', priced: '',
    loading: true, error: null,
    form: null, formError: null, viewing: null, deleting: null,
    saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    state: this.state.visibility || undefined,
    priced: this.state.priced || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminServiceAreas;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load service areas.', 'تعذر تحميل مناطق الخدمة.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0, total: res.total || 0,
      activeCount: res.activeCount || 0,
      outside: res.providersOutsideEveryZone || 0, loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({
    form: { id: null, name: '', description: '', isActive: true, coordinates: '' },
    formError: null, notice: null
  });

  /*
   * Menyunting mengirim koordinat kembali ke addLocation, dan mutation itu
   * MEWAJIBKAN koordinat. Poligon yang tersimpan diubah balik ke bentuk JSON
   * yang sama seperti yang dikirim peta, supaya menyunting nama saja tidak
   * pernah menulis ulang batasnya menjadi sesuatu yang lain.
   */
  openEdit = r => this.setState({
    form: {
      id: r.id, name: r.name, description: r.description || '',
      isActive: r.isActive,
      coordinates: JSON.stringify(
        String(r.outline || '').trim().split(/\s+/)
          .map(p => p.split(',').map(Number))
          .filter(p => p.length === 2 && !isNaN(p[0]))
          // Cincin poligon menutup dengan mengulang titik pertama; parser
          // menambahkannya lagi saat menyimpan, jadi yang terakhir dibuang.
          .slice(0, -1)
          .map(([lat, lng]) => ({ lat, lng }))
      ),
      lockedOffReason: r.lockedOffReason
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    if (!f.name.trim() || !f.description.trim() || !f.coordinates.trim()) {
      this.setState({ formError: this.tx('Name, description and boundary are all required.', 'الاسم والوصف والحدود مطلوبة.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      LocationName: f.name.trim(),
      description: f.description.trim(),
      coordinates: f.coordinates.trim(),
      isActive: !!f.isActive
    }).catch(() => null);
    const res = json && json.data && json.data.addLocation;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the area.', 'تعذر حفظ المنطقة.') });
      return;
    }
    this.setState({
      form: null,
      notice: { tone: 'info', text: f.id ? `${f.name.trim()} ${this.tx('updated.', 'تم تحديثها.')}` : `${f.name.trim()} ${this.tx('created.', 'تم إنشاؤها.')}` }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deleteLocation;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the area.', 'تعذر حذف المنطقة.') }
        : { tone: 'danger', text: `${r.name} ${this.tx('was deleted.', 'تم حذفها.')}` }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      {
        key: 'name', label: t('Area', 'المنطقة'), sortable: true,
        render: r => (
          <span>
            <b>{r.name}</b>
            {r.vertices === 5 && (
              // Poligon lima titik itu persegi panjang. Untuk zona dispatch
              // artinya batasnya kotak, bukan bentuk daerah sebenarnya.
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}> · {t('box', 'مربع')}</span>
            )}
          </span>
        )
      },
      {
        key: 'description', label: t('Note', 'ملاحظة'),
        render: r => r.description
          ? <span title={r.description}>{r.description}</span>
          : <span className="muted">—</span>
      },
      { key: 'providerCount', label: t('Providers inside', 'مقدمو الخدمة داخلها'), align: 'end', sortable: true },
      {
        key: 'areaKm2', label: t('Size', 'المساحة'), align: 'end', sortable: true,
        render: r => <span className="num">{km2(r.areaKm2)} km²</span>
      },
      {
        key: 'activePricingCount', label: t('Price rules', 'قواعد الأسعار'), align: 'end', sortable: true,
        render: r => r.activePricingCount
          ? r.activePricingCount
          : <span className="tag danger"><i />{t('Unbookable', 'غير متاحة للحجز')}</span>
      },
      {
        key: 'isActive', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.isActive ? 'ok' : ''}`}>
            <i />{r.isActive ? t('Accepting', 'تستقبل الحجوزات') : t('Closed', 'مغلقة')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="View boundary"
              aria-label={`View ${r.name} boundary`}
              onClick={() => this.setState({ viewing: r })}>
              <Icon name="pin" />
            </button>
            <button className="btn icon" type="button" title="Edit area"
              aria-label={`Edit ${r.name}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedDeleteReason || 'Delete area'}
              aria-label={`Delete ${r.name}`}
              disabled={!!r.lockedDeleteReason}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError } = this.state;
    const t = this.tx;

    return (
      <Modal
        icon="pin" wide
        title={f.id ? `${t('Edit', 'تعديل')} ${f.name}` : t('Add service area', 'إضافة منطقة خدمة')}
        sub={t('A zone is a polygon. Bookings and provider matching depend on it.', 'المنطقة مضلع وتعتمد عليه الحجوزات ومطابقة مقدمي الخدمة.')}
        confirmLabel={f.id ? t('Save area', 'حفظ المنطقة') : t('Create area', 'إنشاء المنطقة')}
        busy={saving}
        disabled={!f.name.trim() || !f.description.trim() || !f.coordinates.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <Fieldset title={t('Area', 'المنطقة')}>
          <Field label={t('Area name', 'اسم المنطقة')} required>
            <input className="inp" value={f.name} placeholder="Al Warqa"
              onChange={e => this.setForm('name', e.target.value)} />
          </Field>
          <Field label={t('Note', 'ملاحظة')} required
            hint={t("Free text. The stored zones use it for things like 'Zone 1' or 'Industry'.", 'نص حر؛ تستخدم المناطق المحفوظة قيماً مثل المنطقة 1 أو صناعية.') }>
            <input className="inp" value={f.description}
              onChange={e => this.setForm('description', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Boundary', 'الحدود')}
          desc={t('At least four corners, as latitude and longitude pairs. This is the same format the map drawing tool produces.', 'أربع زوايا على الأقل بصيغة أزواج خط العرض والطول، وهي نفس صيغة أداة رسم الخريطة.') }>
          <Field label={t('Coordinates', 'الإحداثيات')} required span
            hint='[{"lat":25.11,"lng":55.20},{"lat":25.14,"lng":55.20}, …]'>
            <textarea className="inp" rows={4} value={f.coordinates}
              onChange={e => this.setForm('coordinates', e.target.value)} />
          </Field>
          <Field label={t('Preview', 'المعاينة')} span>
            <Outline points={
              (() => {
                try {
                  return JSON.parse(f.coordinates || '[]')
                    .map(p => `${p.lat},${p.lng}`).join(' ');
                } catch (e) { return ''; }
              })()
            } size={200} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Availability', 'التوفر')} rows>
          <SettingRow label={t('Accepting bookings', 'تستقبل الحجوزات')}
            hint={t('A closed zone stops the apps taking bookings there.', 'المنطقة المغلقة تمنع التطبيقات من قبول الحجوزات فيها.')}
            note={f.isActive && f.lockedOffReason ? `${t('Locked', 'مقفل')}: ${f.lockedOffReason}` : null}>
            <Switch checked={f.isActive} label={t('Accepting bookings', 'تستقبل الحجوزات')}
              disabled={f.isActive && !!f.lockedOffReason}
              onChange={v => this.setForm('isActive', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, activeCount, outside, page, perPage, loading, error,
      visibility, priced, form, viewing, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="areas"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Service areas', 'مناطق الخدمة')]}
        title={t('Service areas', 'مناطق الخدمة')}
        lead={this.locale() === 'ar' ? `${total} منطقة، ${activeCount} تستقبل الحجوزات.` : `${total} zone${total === 1 ? '' : 's'}, ${activeCount} accepting bookings.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/location" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add area', 'إضافة منطقة')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {/* Provider di luar semua zona tidak akan pernah cocok dengan pencarian
            provider terdekat berbasis zona. Angkanya tidak muncul di layar mana
            pun sebelum ini. */}
        {!loading && !error && outside > 0 && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {this.locale() === 'ar' ? `${outside} من مقدمي الخدمة موجودون خارج جميع المناطق المفتوحة. لن تصل إليهم المطابقة حسب المنطقة.` : `${outside} provider${outside === 1 ? ' is' : 's are'} located outside every open zone. Zone-based matching will never reach them.`}
            </span>
          </div>
        )}

        {form && this.renderForm()}

        {viewing && (
          <Modal
            icon="pin"
            title={viewing.name}
            sub={viewing.description || t('No note', 'لا توجد ملاحظة')}
            hideConfirm
            onClose={() => this.setState({ viewing: null })}
          >
            <div style={{ display: 'grid', placeItems: 'center', padding: '8px 0' }}>
              <Outline points={viewing.outline} size={220} />
            </div>
            <Summary pairs={[
              [t('Corners', 'الزوايا'), `${viewing.vertices}${viewing.vertices === 5 ? ` (${t('a rectangle', 'مستطيل')})` : ''}`],
              [t('Size', 'المساحة'), `${km2(viewing.areaKm2)} km²`],
              [t('Centre', 'المركز'), <span className="mono">{viewing.centreLat}, {viewing.centreLng}</span>],
              [t('Providers inside', 'مقدمو الخدمة داخلها'), String(viewing.providerCount)],
              [t('Price rules', 'قواعد الأسعار'), `${viewing.activePricingCount} ${t('active of', 'نشطة من')} ${viewing.pricingCount}`],
              [t('Status', 'الحالة'), viewing.isActive ? t('Accepting bookings', 'تستقبل الحجوزات') : t('Closed', 'مغلقة')]
            ]} />
          </Modal>
        )}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={`${t('Delete', 'حذف')} ${deleting.name}؟`}
            sub={t('The zone and its boundary are removed.', 'سيتم حذف المنطقة وحدودها.')}
            confirmLabel={t('Delete area', 'حذف المنطقة')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Providers inside', 'مقدمو الخدمة داخلها'), String(deleting.providerCount)],
              [t('Price rules', 'قواعد الأسعار'), String(deleting.pricingCount)],
              [t('Size', 'المساحة'), `${km2(deleting.areaKm2)} km²`]
            ]} />
            {deleting.providerCount > 0 && (
              <div className="notice danger" style={{ marginBottom: 0 }}>
                <span className="ni">!</span>
                <span className="nt">
                  {this.locale() === 'ar' ? `${deleting.providerCount} من مقدمي الخدمة داخل هذه المنطقة حالياً. حذفها يزيل الرابط الوحيد بينهم وبين المنطقة.` : `${deleting.providerCount} provider${deleting.providerCount === 1 ? ' is' : 's are'} inside this zone right now. Deleting it removes the only thing tying them to an area.`}
                </span>
              </div>
            )}
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search area name or note', 'ابحث عن اسم المنطقة أو الملاحظة')}
              filters={
                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="inp" value={visibility} onChange={this.pick('visibility')}
                    aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                    <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                    <option value="active">{t('Accepting', 'تستقبل الحجوزات')}</option>
                    <option value="inactive">{t('Closed', 'مغلقة')}</option>
                  </select>
                  <select className="inp" value={priced} onChange={this.pick('priced')}
                    aria-label={t('Filter by pricing', 'تصفية حسب التسعير')}>
                    <option value="">{t('Pricing: all', 'التسعير: الكل')}</option>
                    <option value="priced">{t('Has price rules', 'لديها قواعد أسعار')}</option>
                    <option value="unpriced">{t('No price rules', 'لا توجد قواعد أسعار')}</option>
                  </select>
                </span>
              }
              emptyText={t('No service areas match', 'لا توجد مناطق خدمة مطابقة')}
              emptyHint={t('Try clearing the status or pricing filter.', 'جرّب مسح تصفية الحالة أو التسعير.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(ServiceAreas);
